import { Box, Button, Divider, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography, useTheme } from "@mui/material";
import { IAfterGuiAttachedParams } from "ag-grid-enterprise";
import { QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { CustomFilterProps, useGridFilter } from "ag-grid-react";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { PropagateLoader } from "react-spinners";
import { FormattedMessage, useIntl } from "react-intl";
import { CACHE_DURATION, STALE_DURATION } from "../../../../../DataView/utils/constants";
import { getTypeByKey } from "../../../../../../../config/axios/apis/type/type";
import { BO_CORE_DEFAULT_VALUE, fieldVariant } from "../../../../../../../utils/AppUtils";
import useDebouncedSearchField from "../../../../../../../Hooks/useDebouncedSearchField";
import SyncInfiniteScroll from "../../../../../../../Hooks/useSyncInfiniteScroll/SyncInfiniteScroll";

export interface IApiValueListFilterConfig {
    fetchType: "api.valueList";
    bocoreTypeName: string;
    fieldName: string;
    enableExclusiveFiltering: boolean;
    defaultOptionsFilter?: {
        filter: "contains" | "equals" | "notEqual" | "blank" | "notBlank";
        value: string[]; // more than one value is handled as OR condition
    };
}

interface IGridContext {
    customListFilterMap?: Record<string, IApiValueListFilterConfig>;
}

const doesFilterPass = () => true;
const getModelAsString = (currModel) => (currModel?.filter ? currModel.filter : "");

const queryFn = (ctx: QueryFunctionContext<string[]>) => getTypeByKey(ctx.queryKey[1], false, { signal: ctx.signal });

const SelectValueListCustomFilter: FC<CustomFilterProps<any, IGridContext>> = ({ colDef, context, model, onModelChange }) => {
    const theme = useTheme();
    const { formatMessage } = useIntl();
    const { fieldValue: searchField, searchText: searchedOption, onChange: onChangeSearch, reset } = useDebouncedSearchField(500);

    const filterValueRef = useRef<HTMLInputElement>(null);
    const appliedInitFilters = useRef<boolean>(false);
    const [showAllOptions, setShowAllOptions] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>(model?.filter || "");
    const [filterType, setFilterType] = useState<string>(model?.type || "");

    const config = useMemo(() => {
        if (context?.customListFilterMap && colDef.field && context.customListFilterMap[colDef.field]) {
            return context.customListFilterMap[colDef.field];
        }

        return undefined;
    }, [context.customListFilterMap, colDef.field]);

    const selectFn = useCallback(
        (res) => {
            if (!res?.data) {
                return [];
            }

            const allValueListAttributes = res.data.attributes.filter((e) => e.type === "VALUE_LIST");
            const requestedAttribute = allValueListAttributes.find((att) => att.attrKey === config!.fieldName);

            if (!requestedAttribute) {
                return [];
            }

            if (searchedOption) {
                return requestedAttribute.attributeValues
                    .filter((e) => e.name.toLowerCase().includes(searchedOption.toLowerCase()) && e.name !== BO_CORE_DEFAULT_VALUE)
                    .map((value) => ({ id: value.id, name: value.name }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            }

            if (config?.defaultOptionsFilter && !appliedInitFilters.current) {
                return requestedAttribute.attributeValues
                    .filter((e) => {
                        if (e.name === BO_CORE_DEFAULT_VALUE) {
                            return false;
                        }

                        switch (config.defaultOptionsFilter!.filter) {
                            case "contains": {
                                let result = false;

                                config.defaultOptionsFilter!.value.forEach((val) => {
                                    if (e.name.toLowerCase().includes(val.toLowerCase())) {
                                        result = true;
                                    }
                                });

                                return result;
                            }
                            case "equals": {
                                let result = false;

                                config.defaultOptionsFilter!.value.forEach((val) => {
                                    if (e.name.toLowerCase() === val.toLowerCase()) {
                                        result = true;
                                    }
                                });

                                return result;
                            }
                            case "notEqual": {
                                let result = false;

                                config.defaultOptionsFilter!.value.forEach((val) => {
                                    if (e.name.toLowerCase() !== val.toLowerCase()) {
                                        result = true;
                                    }
                                });

                                return result;
                            }
                            case "blank":
                                return e.name === "";
                            case "notBlank":
                                return e.name !== "";
                            default:
                                return false;
                        }
                    })
                    .map((value) => ({ id: value.id, name: value.name }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            }

            return requestedAttribute.attributeValues
                .filter((e) => e.name !== BO_CORE_DEFAULT_VALUE)
                .map((value) => ({ id: value.id, name: value.name }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        [config, searchedOption, showAllOptions],
    );

    const { data: options, isFetching } = useQuery({
        enabled: config?.fetchType === "api.valueList" && !!config.bocoreTypeName,
        queryKey: ["bocoreType", `${config?.bocoreTypeName}`],
        gcTime: CACHE_DURATION,
        staleTime: STALE_DURATION,
        queryFn,
        select: selectFn,
    });

    const afterGuiDetached = useCallback(() => {
        appliedInitFilters.current = false;
        setShowAllOptions(false);
        setFilterValue("");
        reset();
    }, [reset]);

    const afterGuiAttached = useCallback(
        (params: IAfterGuiAttachedParams | undefined) => {
            if (!params?.suppressFocus) {
                filterValueRef.current?.focus();
            }

            if (model) {
                setFilterValue(model.filter);
                setFilterType(model.type);
            }

            reset();
        },
        [model, reset],
    );

    useGridFilter({
        doesFilterPass,
        afterGuiDetached,
        afterGuiAttached,
        getModelAsString,
    });

    return (
        <Box>
            <FormControl sx={{ marginTop: "0px" }}>
                {config?.enableExclusiveFiltering ? (
                    <Box sx={{ padding: "8px 8px 0px 8px" }}>
                        <RadioGroup
                            value={filterType}
                            row
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                if (filterValue === "") {
                                    setFilterValue(options.length > 0 ? options[0].name : "");
                                }

                                onModelChange({
                                    filter: filterValue || (options.length > 0 ? options[0].name : ""),
                                    type: e.target.value,
                                    filterType: "radioSet",
                                });
                            }}
                        >
                            <FormControlLabel
                                slotProps={{ typography: { variant: "body2" } }}
                                sx={{ marginLeft: "0px" }}
                                value={"equals"}
                                control={<Radio size="small" />}
                                label={<FormattedMessage id="custom-select-grid-filter.label.equals" defaultMessage="Equals" />}
                            />
                            <FormControlLabel
                                slotProps={{ typography: { variant: "body2" } }}
                                sx={{ marginLeft: "0px" }}
                                value={"notEqual"}
                                control={<Radio size="small" />}
                                label={<FormattedMessage id="custom-select-grid-filter.label.not-equal" defaultMessage="Not Equal" />}
                            />
                        </RadioGroup>
                    </Box>
                ) : null}
                <Box sx={{ padding: "0px 8px 8px 8px" }}>
                    <TextField
                        size="small"
                        margin="normal"
                        variant={fieldVariant}
                        value={searchField}
                        onChange={(e) => onChangeSearch(e.target.value)}
                        fullWidth
                        label={<FormattedMessage id="common.button-label.search" defaultMessage="Search" />}
                        disabled={isFetching}
                        placeholder={formatMessage({ id: "custom-select-grid-filter.placeholder.search", defaultMessage: "Search..." })}
                    />
                </Box>
                <Box sx={{ minWidth: "200px", maxHeight: "200px", minHeight: "100px", overflow: "auto" }}>
                    {isFetching ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <PropagateLoader loading={isFetching} color={theme.palette.primary.main} />
                        </Box>
                    ) : (
                        <RadioGroup
                            value={filterValue}
                            onChange={(e) => {
                                setFilterValue(e.target.value);
                                if (filterType === "") {
                                    setFilterType("equals");
                                }
                                onModelChange({
                                    filter: e.target.value,
                                    type: filterType || "equals",
                                    filterType: "radioSet",
                                });
                            }}
                        >
                            {options.length === 0 ? (
                                <Box sx={{ padding: "8px", textAlign: "center" }}>
                                    <Typography variant="body1">
                                        <FormattedMessage id="custom-select-grid-filter.label.no-options" defaultMessage="No options found." />
                                    </Typography>
                                </Box>
                            ) : (
                                <SyncInfiniteScroll allData={options} pageSize={8}>
                                    {options.map((opt) => (
                                        <FormControlLabel
                                            slotProps={{ typography: { variant: "body2" } }}
                                            key={opt.id}
                                            sx={{ marginLeft: "0px" }}
                                            value={opt.name}
                                            control={<Radio size="small" />}
                                            label={opt.name}
                                        />
                                    ))}
                                </SyncInfiniteScroll>
                            )}
                        </RadioGroup>
                    )}
                </Box>
                <Divider />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                    }}
                >
                    {config?.defaultOptionsFilter ? (
                        <Button
                            variant="text"
                            color="primary"
                            onClick={() => {
                                appliedInitFilters.current = !showAllOptions;
                                setShowAllOptions(!showAllOptions);
                                reset();
                            }}
                        >
                            {showAllOptions ? (
                                <FormattedMessage id="common.field-label.show-defaults" defaultMessage="Show Defaults" />
                            ) : (
                                <FormattedMessage id="custom-select-grid-filter.label.see-all-options" defaultMessage="See All Options" />
                            )}
                        </Button>
                    ) : null}
                    <Button
                        variant="text"
                        color="primary"
                        sx={{ marginLeft: "auto" }}
                        onClick={() => {
                            onModelChange(null);
                            setFilterValue("");
                            setFilterType("");
                            reset();
                        }}
                    >
                        <FormattedMessage id="common.button-label.clear" defaultMessage="Clear" />
                    </Button>
                </Box>
            </FormControl>
        </Box>
    );
};

export default SelectValueListCustomFilter;
